/**
 * Cartesian plot for the editor preview — bar, line and area.
 *
 * Mirrors Chart\CartesianRenderer — the parts every bar, line and area chart
 * shares: gridlines, the category bands and the tooltips. The marks themselves
 * come from line-marks.jsx and bar-marks.jsx.
 *
 * Same class names as the server renderer, same custom properties, same
 * fractions, so one stylesheet dresses both and the preview is the page.
 */

import { valueFraction } from './geometry';
import { formatValue } from './format';
import { LineMarks, PointLabels } from './line-marks';
import { BarSlots } from './bar-marks';

export default function CartesianPlot( { config } ) {
	const isLine = config.type === 'line' || config.type === 'area';

	return (
		<>
			{ config.showGrid && <Gridlines config={ config } /> }
			{ isLine && (
				<>
					<LineMarks config={ config } />
					<PointLabels config={ config } />
				</>
			) }
			<Bands config={ config } />
		</>
	);
}

function Gridlines( { config } ) {
	const { scale } = config;

	return (
		<div className="brandy-chart__gridlines" aria-hidden="true">
			{ scale.ticks.map( ( tick, index ) => (
				<span
					key={ index }
					className={ `brandy-chart__gridline${
						tick === 0 && scale.min < 0
							? ' brandy-chart__gridline--zero'
							: ''
					}` }
					style={ {
						'--grid-pos': `${ valueFraction( tick, scale ) * 100 }%`,
					} }
				/>
			) ) }
		</div>
	);
}

function Bands( { config } ) {
	const isBar = config.type === 'bar';

	return (
		<div className="brandy-chart__bands">
			{ config.categories.map( ( category, index ) => (
				<div
					key={ index }
					className="brandy-chart__band"
					style={ { '--band-index': index } }
					tabIndex={ config.tooltip ? 0 : undefined }
				>
					{ isBar && <BarSlots config={ config } index={ index } /> }
					{ config.tooltip && (
						<Tooltip config={ config } index={ index } />
					) }
				</div>
			) ) }
		</div>
	);
}

function Tooltip( { config, index } ) {
	return (
		<span className="brandy-chart__tooltip" role="tooltip">
			<span className="brandy-chart__tooltip-title">
				{ config.categories[ index ] ?? '' }
			</span>
			{ config.series.map( ( series, slot ) => (
				<span key={ slot } className="brandy-chart__tooltip-row">
					<span
						className="brandy-chart__tooltip-key"
						style={ { '--key-color': series.color } }
					/>
					<span className="brandy-chart__tooltip-value">
						{ formatValue( series.data[ index ] ?? 0, config.format ) }
					</span>
					<span className="brandy-chart__tooltip-name">
						{ series.label }
					</span>
				</span>
			) ) }
		</span>
	);
}
